<template>
  <MainHeader />
  <MainSidebar />
  <div
    class="main-content bg_gray d-flex flex-column transition overflow-hidden"
  >
    <BreadcrumbMenu
      pageTitle="create_sale"
      :items="breadcrumbItems"
      :showHome="false"
    />
    <div class="row mb-40">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.date") }}</label>
          <input
            type="date"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            v-model="saleDate"
            readonly
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.reference_no") }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            :placeholder="$t('sales_page.reference_no')"
            v-model="referenceNo"
            readonly
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.customer") }} <span class="text-danger">*</span></label>
          <select 
            class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55"
            v-model="selectedCustomerId"
          >
            <option :value="null">{{ customers.length ? $t("sales_page.select_customer") : $t("sales_page.loading_customers") }}</option>
            <option v-for="customer in customers" :key="customer.id" :value="customer.id">
              {{ customer.name }}
            </option>
          </select>
          <div v-if="stateStore.activePromotion" class="promotion-notice mt-2">
            <strong>{{ stateStore.activePromotion.name }}</strong>
            <span>
              {{ promotionSummary }}
            </span>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.warehouse") }} <span class="text-danger">*</span></label>
          <select 
            class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55"
            v-model="selectedWarehouseId"
          >
            <option :value="null">{{ $t("sales_page.select_warehouse") }}</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
              {{ warehouse.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-12">
        <div class="form-group">
          <label class="d-block fs-14 text-black mb-2">{{ $t("sales_page.choose_product") }}</label>
          <div class="search-area style-two position-relative w-100">
            <div class="position-relative">
              <input
                type="text"
                :placeholder="$t('sales_page.scan_search_product')"
                class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
                v-model="searchQuery"
                @keyup.enter="onSearch"
                @input="onSearchInput"
              />
              <button
                type="button"
                class="bg-transparent border-0 position-absolute top-0 end-0 h-100 pt-0 py-0 px-2"
                @click="onSearch"
              >
                <img src="../../assets/img/icons/search.svg" alt="Image" />
              </button>

              <!-- Search Results Dropdown -->
              <div v-if="searchResults.length > 0" class="position-absolute w-100 bg-white shadow rounded-1 mt-1 z-3 overflow-auto" style="max-height: 300px;">
                <ul class="list-unstyled mb-0">
                  <li 
                    v-for="product in searchResults" 
                    :key="product.id"
                    class="p-2 border-bottom product-search-item"
                    :class="{ 'cursor-pointer hover-bg-light': canSelectProduct(product), 'disabled-product': !canSelectProduct(product) }"
                    @click="selectProduct(product)"
                  >
                    <div class="d-flex justify-content-between">
                      <span class="fw-bold">{{ product.name }}</span>
                      <span class="text-muted">{{ product.code }}</span>
                    </div>
                    <div class="d-flex justify-content-between fs-12 text-muted">
                      <span>{{ $t("sales_page.price") }}: ${{ product.price }}</span>
                      <span v-if="Number(product.type) === 1">
                        {{ getAvailableStock(product) > 0 ? `${$t("sales_page.stock")}: ${getAvailableStock(product)}` : $t("sales_page.out_of_stock") }}
                      </span>
                      <span v-else>{{ $t("sales_page.service_item") }}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SelectedProducts />
    <SubmitPurchase />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div
    class="delete-popup offcanvas offcanvas-end border-0"
    tabindex="-1"
    id="deletePopup"
  >
    <div class="offcanvas-body p-0">
      <div class="delete-success">
        <img src="../../assets/img/icons/tick-circle.svg" alt="Image" />
        <span class="text-white fw-medium">
          {{ $t("sales_page.product_deleted_from_list") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import SelectedProducts from "../../components/Sales/CreateSales/SelectedProducts.vue";
import SubmitPurchase from "../../components/Sales/CreateSales/SubmitPurchase.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";

import customerService, { type Customer } from "../../services/customer";
import warehouseService, { type Warehouse } from "../../services/warehouse";
import productService from "../../services/product";
import salesService from "../../services/sales";
import settingService from "../../services/setting";
import promotionService from "../../services/promotion";
import stateStore from "../../utils/store";
import Swal from "sweetalert2";
import {
  formatPromotionValue,
  resolveBestPromotion,
  type PromotionRecord,
} from "../../utils/promotion";
import { calculateLineTotal } from "../../utils/posPricing";
import { hasPermission } from "../../utils/permission";

export default defineComponent({
  name: "CreateSalesPage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    SelectedProducts,
    SubmitPurchase,
    MainFooter,
  },
  setup() {
    const { t } = useI18n();
    const customers = ref<Customer[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const searchQuery = ref("");
    const searchResults = ref<any[]>([]);
    const saleDate = ref(new Date().toISOString().split('T')[0]);
    const promotions = ref<PromotionRecord[]>([]);
    const manualOrderDiscount = ref(0);
    const manualOrderDiscountType = ref<'fixed' | 'percentage'>('fixed');
    const canUsePromotion = hasPermission("promotion_use");
    const breadcrumbItems = [
      { label: "sales_list", to: "/sales-list" },
      { label: "create_sale" },
    ];

    const selectedCustomerId = computed({
      get: () => stateStore.selectedCustomerId,
      set: (val: number | null) => {
        stateStore.selectedCustomerId = val ? Number(val) : null;
      }
    });

    const selectedWarehouseId = computed({
      get: () => stateStore.selectedWarehouseId,
      set: (val: number | null) => {
        stateStore.selectedWarehouseId = val ? Number(val) : null;
      }
    });

    const referenceNo = computed({
      get: () => stateStore.referenceNo,
      set: (val: string) => {
        stateStore.referenceNo = val;
      }
    });

    const fetchData = async () => {
      try {
        console.log("Fetching initial data...");
        if (!canUsePromotion) {
          stateStore.setActivePromotion(null);
        }
        const [customerRes, warehouseRes, nextRefRes, settingsRes, promotionRes] = await Promise.allSettled([
          customerService.getCustomers(),
          warehouseService.getWarehouses(),
          salesService.getNextReference(),
          settingService.getSettings(),
          canUsePromotion
            ? promotionService.getAvailablePromotions()
            : Promise.resolve([] as PromotionRecord[]),
        ]);
        
        if (customerRes.status === 'fulfilled') {
          customers.value = customerRes.value;
          console.log("Customers loaded:", customers.value.length);
        } else {
          console.error("Error fetching customers:", customerRes.reason);
        }

        if (warehouseRes.status === 'fulfilled') {
          warehouses.value = warehouseRes.value;
          console.log("Warehouses loaded:", warehouses.value.length);
        } else {
          console.error("Error fetching warehouses:", warehouseRes.reason);
        }

        if (nextRefRes.status === 'fulfilled') {
          stateStore.referenceNo = nextRefRes.value;
          console.log("Reference loaded:", nextRefRes.value);
        }

        if (settingsRes.status === 'fulfilled' && settingsRes.value) {
          const s = settingsRes.value;
          console.log("Settings loaded:", s);
          
          // Set defaults from settings if store is empty
          if (!stateStore.selectedCustomerId && s.default_customer) {
            const cid = Number(s.default_customer);
            console.log("Setting default customer from settings:", cid);
            stateStore.selectedCustomerId = cid;
          }
          if (!stateStore.selectedWarehouseId && s.default_warehouse) {
            const wid = Number(s.default_warehouse);
            console.log("Setting default warehouse from settings:", wid);
            stateStore.selectedWarehouseId = wid;
          }
        }

        if (promotionRes.status === 'fulfilled') {
          promotions.value = promotionRes.value;
        } else {
          console.error("Error fetching promotions:", promotionRes.reason);
          promotions.value = [];
          stateStore.setActivePromotion(null);
        }

        // Final sync log
        console.log("Data fetch complete. Store state:", {
          customer: stateStore.selectedCustomerId,
          warehouse: stateStore.selectedWarehouseId,
          reference: stateStore.referenceNo
        });

        // Load data from localStorage (e.g., from Quotation conversion)
        const storedProducts = localStorage.getItem("pos_selected_products");
        if (storedProducts) {
          try {
            const products = JSON.parse(storedProducts);
            if (Array.isArray(products) && products.length > 0) {
              stateStore.selectedProducts = products;
              localStorage.removeItem("pos_selected_products"); // Clear after loading
            }
          } catch (e) {
            console.error("Failed to parse stored products:", e);
          }
        }

        const storedCustomer = localStorage.getItem("pos_customer_id");
        if (storedCustomer) {
          stateStore.selectedCustomerId = Number(storedCustomer);
          localStorage.removeItem("pos_customer_id");
        }

        const storedWarehouse = localStorage.getItem("pos_warehouse_id");
        if (storedWarehouse) {
          stateStore.selectedWarehouseId = Number(storedWarehouse);
          localStorage.removeItem("pos_warehouse_id");
        }
        
        const storedTax = localStorage.getItem("pos_order_tax");
        if (storedTax) {
          stateStore.orderTax = Number(storedTax);
          stateStore.orderTaxType = 'fixed';
          localStorage.removeItem("pos_order_tax");
        }
        
        const storedDiscount = localStorage.getItem("pos_order_discount");
        if (storedDiscount) {
          stateStore.orderDiscount = Number(storedDiscount);
          stateStore.orderDiscountType = 'fixed';
          localStorage.removeItem("pos_order_discount");
        }
        
        const storedShipping = localStorage.getItem("pos_shipping");
        if (storedShipping) {
          stateStore.shippingCost = Number(storedShipping);
          localStorage.removeItem("pos_shipping");
        }

      } catch (error) {
        console.error("Unexpected error in fetchData:", error);
      }
    };

    const getAvailableStock = (product: any) => {
      if (Number(product.type) !== 1) {
        return Number(product.qty ?? 0);
      }

      const stocks = Array.isArray(product.stocks) ? product.stocks : [];
      if (!stocks.length) {
        return 0;
      }

      const warehouseId = selectedWarehouseId.value;
      if (!warehouseId) {
        return stocks.reduce(
          (sum: number, stock: any) => sum + Number(stock.qty ?? stock.quantity ?? 0),
          0
        );
      }

      return stocks
        .filter((stock: any) => Number(stock.warehouse_id) === Number(warehouseId))
        .reduce((sum: number, stock: any) => sum + Number(stock.qty ?? stock.quantity ?? 0), 0);
    };

    const canSelectProduct = (product: any) => {
      if (Number(product.type) !== 1) return true;
      return getAvailableStock(product) > 0;
    };

    const fetchProductResults = async (rawQuery: string) => {
      const isSpaceOnly = rawQuery.length > 0 && rawQuery.trim().length === 0;
      const searchValue = isSpaceOnly ? undefined : rawQuery;
      const products = await productService.getProducts({
        search: searchValue,
        warehouse_id: selectedWarehouseId.value ?? undefined,
      });
      searchResults.value = products;
      return products;
    };

    const onSearch = async () => {
      try {
        if (!searchQuery.value.length) {
          searchResults.value = [];
          return;
        }

        const products = await fetchProductResults(searchQuery.value);
        if (products.length > 0) {
          if (products.length === 1 && searchQuery.value.trim().length > 0 && canSelectProduct(products[0])) {
            // If only one match, select it immediately
            selectProduct(products[0]);
          }
        } else {
          await Swal.fire({
            icon: "warning",
            title: t("sales_page.no_product_found"),
            text: t("sales_page.no_matching_product"),
          });
          searchResults.value = [];
        }
      } catch (error) {
        console.error("Error searching product:", error);
      }
    };

    const onSearchInput = async () => {
      if (!searchQuery.value.length) {
        searchResults.value = [];
        return;
      }
      try {
        await fetchProductResults(searchQuery.value);
      } catch (error) {
        console.error("Error searching product:", error);
      }
    };

    const selectProduct = (product: any) => {
      if (!canSelectProduct(product)) return;
      stateStore.addSelectedProduct(product);
      searchQuery.value = "";
      searchResults.value = [];
    };

    const subTotal = computed(() => {
      return stateStore.selectedProducts.reduce((sum, product) => {
        return sum + calculateLineTotal({
          unitPrice: Number(product.price || 0),
          quantity: Number(product.qty || 0),
          discount: Number(product.discount || 0),
          discountType: product.discount_type,
        });
      }, 0);
    });

    const promotionSummary = computed(() => {
      const activePromotion = stateStore.activePromotion;
      if (!activePromotion) return "";

      const valueLabel = formatPromotionValue(activePromotion);
      return activePromotion.customer_name
        ? t("sales_page.promotion_auto_applies_for_customer", {
            value: valueLabel,
            customer: activePromotion.customer_name,
          })
        : t("sales_page.promotion_auto_applies_matching_customers", {
            value: valueLabel,
          });
    });

    watch(
      () => [stateStore.orderDiscount, stateStore.orderDiscountType, stateStore.activePromotion] as const,
      ([orderDiscount, orderDiscountType, activePromotion]) => {
        if (!activePromotion) {
          manualOrderDiscount.value = Number(orderDiscount || 0);
          manualOrderDiscountType.value = orderDiscountType as 'fixed' | 'percentage';
        }
      },
      { immediate: true }
    );

    watch(
      [promotions, selectedCustomerId, subTotal],
      ([promotionList, customerId, subtotal]) => {
        const nextPromotion = resolveBestPromotion(promotionList, {
          customerId,
          subtotal,
        });

        if (nextPromotion) {
          stateStore.setActivePromotion(nextPromotion);
          stateStore.setOrderDiscount(
            Number(nextPromotion.value || 0),
            nextPromotion.discount_type
          );
          return;
        }

        if (stateStore.activePromotion) {
          stateStore.setOrderDiscount(
            manualOrderDiscount.value,
            manualOrderDiscountType.value
          );
        }
        stateStore.setActivePromotion(null);
      },
      { immediate: true }
    );

    onMounted(async () => {
      await fetchData();
    });

    return {
      customers,
      warehouses,
      breadcrumbItems,
      searchQuery,
      searchResults,
      selectedCustomerId,
      selectedWarehouseId,
      saleDate,
      referenceNo,
      promotionSummary,
      getAvailableStock,
      canSelectProduct,
      onSearch,
      onSearchInput,
      selectProduct,
      stateStore
    };
  }
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.hover-bg-light:hover {
  background-color: #f8f9fa;
}
.z-3 {
  z-index: 1050;
}

.disabled-product {
  opacity: 0.55;
  cursor: not-allowed;
  background-color: #f8f9fa;
}

.promotion-notice {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #eef6ff;
  color: #1d4ed8;
  font-size: 13px;
}
</style>
